import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { ProgressiveTypeaheadDirective } from '../../../directives/progressive-typeahead.directive';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatChipsModule } from '@angular/material/chips';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FormsModule } from '@angular/forms';
import { PyramidDto } from '../../../models/api/pyramidDto';
import { LargeShareholderCategory } from '../../../models/largeShareholderCategory';
import { LutService } from '../../../services/lut.service';
import { PyramidEntitySummary } from '../../../models/pyramidEntitySummary';
import { PyramidalItemCardComponent, DeleteEntityYearEvent } from '../pyramidal-item-card/pyramidal-item-card.component';
import { PyramidsYearCardComponent, DeletePyramidYearEvent } from '../pyramids-year-card/pyramids-year-card.component';
import { PyramidEntriesByYearDto } from '../../../models/api/pyramidEntriesByYearDto';
import { Helpers } from '../../../utilities/helpers';

interface PyramidDisplayItem {
  id: string;
  directorId?: string;
  pyramidalShareholderId?: string;
  pyramidLargeShareholderId?: string;
  companyId?: string;
  opbCompanyId?: string;
  name: string;
  type: string;
  category: string;
  subcategory: string;
  percentage: number;
}

@Component({
  selector: 'app-large-shareholder-pyramid',
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatSortModule,
    MatIconModule,
    MatSelectModule,
    ProgressiveTypeaheadDirective,
    MatFormFieldModule,
    MatChipsModule,
    MatButtonModule,
    MatTooltipModule,
    FormsModule,
    PyramidalItemCardComponent,
    PyramidsYearCardComponent
  ],
  templateUrl: './large-shareholder-pyramid.component.html',
  styleUrls: ['./large-shareholder-pyramid.component.scss']
})
export class LargeShareholderPyramidComponent implements OnInit, OnChanges {
  @Input() pyramid: PyramidDto[] = [];
  @Input() pyramidEntitySummaries: PyramidEntitySummary[] = [];
  @Input() selectedYear?: number;
  @Input() pyramidEntriesByYear?: PyramidEntriesByYearDto;
  @Output() editPyramidItem = new EventEmitter<string>();
  @Output() deletePyramidItem = new EventEmitter<string>();
  @Output() deleteEntityYear = new EventEmitter<DeleteEntityYearEvent>();
  @Output() deletePyramidYear = new EventEmitter<DeletePyramidYearEvent>();

  private lutService = inject(LutService);
  private router = inject(Router);
  protected readonly Helpers = Helpers;

  dataSource = new MatTableDataSource<PyramidDisplayItem>([]);
  displayedColumns: string[] = ['name', 'type', 'category', 'subcategory', 'percentage', 'actions'];

  categories: LargeShareholderCategory[] = [];
  availableYears: number[] = [];
  selectedYears: number[] = [];
  showEntityTextView = false;
  showYearTextView = false;

  ngOnInit() {
    // Load categories
    this.lutService.getLargeShareholderCategories().subscribe(data => {
      if (data) {
        this.categories = data;
      }
    });

    // Load available years from measurements
    this.availableYears = Helpers.measurements().map(y => parseInt(y, 10)).sort((a, b) => b - a);

    // Initialize selected years with the year from Basic Information
    if (this.selectedYear) {
      this.selectedYears = [this.selectedYear];
    }

    // Process initial data
    this.processData();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['pyramid']) {
      this.processData();
    }

    // Update selected years when selectedYear input changes
    if (changes['selectedYear'] && this.selectedYear) {
      // Only update if the year is not already selected
      if (!this.selectedYears.includes(this.selectedYear)) {
        this.selectedYears = [this.selectedYear];
      }
    }
  }

  getYearPyramids(year: number): PyramidDto[] {
    if (!year || !this.pyramidEntriesByYear) {
      return [];
    }
    return this.pyramidEntriesByYear[year.toString()] || [];
  }

  onYearsSelectionChange() {
    // Ensure selected years are sorted in descending order
    this.selectedYears.sort((a, b) => b - a);
  }

  getYearName(year: number): string {
    return year.toString();
  }

  selectAllYears() {
    if (this.pyramidEntriesByYear) {
      const pyramidYears = Object.keys(this.pyramidEntriesByYear).map(y => parseInt(y, 10));
      this.selectedYears = pyramidYears.length > 0 ? pyramidYears.sort((a, b) => b - a) : [];
    } else {
      this.selectedYears = [];
    }
  }

  deselectAllYears() {
    this.selectedYears = [];
  }

  getSelectedYearsArray(): number[] {
    return this.selectedYears || [];
  }

  compareYears(year1: number, year2: number): boolean {
    return year1 === year2;
  }

  toggleEntityView() {
    this.showEntityTextView = !this.showEntityTextView;
  }

  toggleYearView() {
    this.showYearTextView = !this.showYearTextView;
  }

  private processData() {
    if (!this.pyramid || this.pyramid.length === 0) {
      this.dataSource.data = [];
      return;
    }

    const displayItems: PyramidDisplayItem[] = this.pyramid.map(pyramid => ({
      id: pyramid.id,
      directorId: pyramid.directorId || undefined,
      pyramidalShareholderId: pyramid.pyramidalShareholderId || undefined,
      pyramidLargeShareholderId: pyramid.pyramidLargeShareholderId || undefined,
      companyId: pyramid.companyId || undefined,
      opbCompanyId: pyramid.opbCompanyId || undefined,
      name: this.getPyramidName(pyramid),
      type: this.getPyramidType(pyramid),
      category: pyramid.categoryName || '',
      subcategory: pyramid.subCategoryName || '',
      percentage: pyramid.percentage
    }));

    // Sort by percentage descending
    displayItems.sort((a, b) => b.percentage - a.percentage);

    this.dataSource.data = displayItems;
  }

  private getPyramidName(pyramid: PyramidDto): string {
    return pyramid.pyramidLargeShareholderName
      || pyramid.pyramidalShareholderName
      || pyramid.companyName
      || pyramid.opbCompanyName
      || pyramid.directorName
      || '';
  }

  private getPyramidType(pyramid: PyramidDto): string {
    if (pyramid.pyramidLargeShareholderId) return 'Large Shareholder';
    if (pyramid.pyramidalShareholderId) return 'Pyramidal Shareholder';
    if (pyramid.companyId) return 'Company';
    if (pyramid.opbCompanyId) return 'OPB Company';
    if (pyramid.directorId) return 'Director';
    return 'Unknown';
  }

  formatPercentage(value: number): string {
    return value ? `${value.toFixed(2)}%` : '';
  }

  getTotalPercentage(): number {
    return this.dataSource.data.reduce((sum, item) => sum + (item.percentage || 0), 0);
  }

  onRowClick(row: PyramidDisplayItem) {
    if (row.type === 'Director' && row.directorId) {
      // Preserve current tab in URL for back navigation
      const currentUrl = this.router.url;
      this.router.navigate(['/directors/edit', row.directorId], {
        state: { returnUrl: currentUrl }
      });
    } else if (row.type === 'Pyramidal Shareholder' && row.pyramidalShareholderId) {
      // Preserve current tab in URL for back navigation
      const currentUrl = this.router.url;
      this.router.navigate(['/pyramidal-shareholders/edit', row.pyramidalShareholderId], {
        state: { returnUrl: currentUrl }
      });
    }
  }

  isClickable(row: PyramidDisplayItem): boolean {
    return (row.type === 'Director' && !!row.directorId) ||
           (row.type === 'Pyramidal Shareholder' && !!row.pyramidalShareholderId);
  }

  /**
   * Builds the edit-page URL for a pyramid node based on its entity type, so it can be
   * opened in a new tab. Returns '' when the node has no navigable target (e.g. Unknown),
   * in which case the open-in-new-tab icon is hidden.
   */
  getEntityUrl(row: PyramidDisplayItem): string {
    const yearParams = this.selectedYear ? { queryParams: { year: this.selectedYear } } : {};
    let urlTree;
    switch (row.type) {
      case 'Large Shareholder':
        if (!row.pyramidLargeShareholderId) return '';
        urlTree = this.router.createUrlTree(['/large-shareholders/edit', row.pyramidLargeShareholderId], yearParams);
        break;
      case 'Pyramidal Shareholder':
        if (!row.pyramidalShareholderId) return '';
        urlTree = this.router.createUrlTree(['/pyramidal-shareholders/edit', row.pyramidalShareholderId]);
        break;
      case 'Company':
        if (!row.companyId) return '';
        urlTree = this.router.createUrlTree(['/companies/edit', row.companyId], yearParams);
        break;
      case 'OPB Company':
        if (!row.opbCompanyId) return '';
        urlTree = this.router.createUrlTree(['/companies/opb-edit', row.opbCompanyId]);
        break;
      case 'Director':
        if (!row.directorId) return '';
        urlTree = this.router.createUrlTree(['/directors/edit', row.directorId]);
        break;
      default:
        return '';
    }
    return this.router.serializeUrl(urlTree);
  }

  onEditClick(row: PyramidDisplayItem, event: Event) {
    event.stopPropagation();
    this.editPyramidItem.emit(row.id);
  }

  onDeleteClick(row: PyramidDisplayItem, event: Event) {
    event.stopPropagation();
    this.deletePyramidItem.emit(row.id);
  }

  onDeleteEntityYear(event: DeleteEntityYearEvent) {
    this.deleteEntityYear.emit(event);
  }

  onDeletePyramidYear(event: DeletePyramidYearEvent) {
    this.deletePyramidYear.emit(event);
  }
}
